import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';
import { TaskTodoSubtype } from 'crono-fe-common/types/enums/taskTodoSubtype';

import { Template } from 'crono-fe-common/types/template';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { CronoAttachment } from 'crono-fe-common/types/cronoAttachment';
import { FileContent } from 'use-file-picker';
import { Account } from 'crono-fe-common/types/account';
import { Prospect } from 'crono-fe-common/types/prospect';
import { Opportunity } from 'crono-fe-common/types/DTO/opportunityViewResponseDTO';
import { fullName } from 'crono-fe-common/utils';
import { mapTaskTypeToTemplateType } from 'utils/businessLogic';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import { TemplateStep } from 'crono-fe-common/types/enums/templateStep';

import * as Yup from 'yup';
import { FormikErrors } from 'formik';

export interface TaskOption {
  type: TaskTodoType;
  subtype?: TaskTodoSubtype;
}

export interface TaskFormValues {
  contactId: string | null;
  companyId: string | null;
  searchCompanyText: string;
  searchContactText: string;
  opportunityId: string | null;
  searchOpportunityText: string;
  title: string;
  date: Date;
  taskOption: TaskOption;
  template: Template;
  automatic: boolean;
  initialTemplate: Template;
  ccAddresses?: string[];
  selectedEmail?: string | null;
  threadId?: string | null;
  duration: number;
  recording: Blob | null;
  attachments?: (FileContent | CronoAttachment)[];
}

export function getEmptyTemplate(taskOption: TaskOption): Template {
  const type = mapTaskTypeToTemplateType(taskOption.type, taskOption.subtype);
  if (!type) {
    return {
      type: TemplateType.SCRIPT,
      title: '',
      subject: '',
      content: '',

      //dummy data to make placeholder work
      id: -1,
      userId: -1,
      subscriptionId: -1,
      language: '',
      shared: false,
      step: TemplateStep.FIRST_TOUCH,
      useCount: 0,
      openCount: 0,
      answerCount: 0,
    };
  }

  return {
    type,
    title: '',
    subject: '',
    content: '',

    //dummy data to make placeholder work
    id: -1,
    userId: -1,
    subscriptionId: -1,
    language: '',
    shared: false,
    step: TemplateStep.FIRST_TOUCH,
    useCount: 0,
    openCount: 0,
    answerCount: 0,
  };
}

export function getEditTaskDefaultValues(
  initialTask: TaskTodo,
): TaskFormValues {
  const taskOption: TaskOption = {
    type: initialTask.type,
    subtype: initialTask.subtype,
  };

  const template = initialTask.template
    ? {
        ...initialTask.template,
      }
    : getEmptyTemplate(taskOption);

  if (initialTask.description) {
    template.content = initialTask.description;
  }

  return {
    contactId: initialTask.prospectId ?? initialTask.prospect?.objectId ?? null,
    companyId: initialTask.accountId ?? initialTask.account?.objectId ?? null,
    searchCompanyText: initialTask.account?.name ?? '',
    searchContactText:
      initialTask.prospect?.name ??
      fullName(
        initialTask.prospect?.firstName,
        initialTask.prospect?.lastName,
      ) ??
      '',
    opportunityId: initialTask.opportunityId ?? null,
    searchOpportunityText: initialTask.opportunity?.name ?? '',
    title: initialTask.subject ?? '',
    date: initialTask.activityDate
      ? new Date(initialTask.activityDate)
      : new Date(),
    taskOption,
    template,
    automatic: initialTask.automatic ?? false,
    initialTemplate: structuredClone(template),
    ccAddresses: initialTask.ccAddresses ?? [],
    duration: 0,
    recording: null,
    attachments: [],
  };
}

interface GetCreateTaskDefaultValuesParams {
  initialTaskOption?: TaskOption;
  initialCompany?: Account;
  initialContact?: Prospect;
  initialOpportunity?: Opportunity;
  showUpgradeBadges: boolean;
  initialDate?: Date;
}

export function getCreateTaskDefaultValues({
  initialTaskOption,
  initialCompany,
  initialContact,
  initialOpportunity,
  showUpgradeBadges,
  initialDate,
}: GetCreateTaskDefaultValuesParams): TaskFormValues {
  // Creating a new task - use initial values provided or defaults
  let defaultTaskOption: TaskOption;

  if (initialTaskOption) {
    // Use provided task option
    defaultTaskOption = initialTaskOption;
  } else if (showUpgradeBadges) {
    // Default to LinkedIn Message
    defaultTaskOption = {
      type: TaskTodoType.LINKEDIN,
      subtype: TaskTodoSubtype.LinkedinMessage,
    };
  } else {
    // Default to Email
    defaultTaskOption = {
      type: TaskTodoType.EMAIL,
    };
  }

  return {
    contactId: initialContact?.objectId ?? null,
    companyId: initialContact?.accountId ?? initialCompany?.objectId ?? null,
    searchCompanyText: initialCompany?.name ?? '',
    searchContactText:
      initialContact?.name ??
      fullName(initialContact?.firstName, initialContact?.lastName) ??
      '',
    opportunityId: initialOpportunity?.objectId ?? null,
    searchOpportunityText: initialOpportunity?.name ?? '',
    title: '',
    date: initialDate ?? new Date(),
    taskOption: defaultTaskOption,
    template: getEmptyTemplate(defaultTaskOption),
    initialTemplate: structuredClone(getEmptyTemplate(defaultTaskOption)),
    automatic: false,
    duration: 0,
    recording: null,
    attachments: [],
  };
}

export const taskUpsertFormValidationSchema = Yup.object({
  companyId: Yup.string().required('Company is required'),
  contactId: Yup.string()
    .nullable()
    .when('taskOption.type', {
      is: TaskTodoType.GENERIC,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required('Contact is required'),
    }),
  title: Yup.string().required('Title is required'),

  // content requirement handled via root-level test below
  template: Yup.object({
    content: Yup.string().nullable(),
    recordingDuration: Yup.number().nullable(),
  }),

  // voice note recording requirement handled via root-level test below
  recording: Yup.mixed().nullable(),

  // needed so "taskOption.subtype" exists in schema path resolution
  taskOption: Yup.object({
    subtype: Yup.mixed<TaskTodoSubtype>().nullable(),
    type: Yup.mixed<TaskTodoType>().nullable(),
  }),
});

export const getFirstError = (errors: FormikErrors<unknown>): string | null => {
  const walk = (obj: Record<string, unknown>): string | null => {
    for (const key in obj) {
      const value = obj[key];
      if (typeof value === 'string') return value;
      if (value && typeof value === 'object') {
        const nested = walk(value as Record<string, unknown>);
        if (nested) return nested;
      }
    }
    return null;
  };
  return walk(errors as Record<string, unknown>);
};
